/**
 * Business Locations API Layer.
 *
 * React Query hooks for CRUD operations on organization business locations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";

export interface BusinessLocation {
  id: string;
  organization_id: string;
  name: string;
  location_type: string;
  is_primary: boolean;
  stores_inventory: boolean;
  address: Record<string, string> | null;
  phone: string | null;
  email: string | null;
  default_tax_rate: number;
  notes: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface BusinessLocationCreate {
  name: string;
  location_type?: string;
  is_primary?: boolean;
  stores_inventory?: boolean;
  address?: Record<string, string>;
  phone?: string;
  email?: string;
  default_tax_rate?: number;
  notes?: string;
  sort_order?: number;
}

export function useBusinessLocations() {
  return useQuery<BusinessLocation[]>({
    queryKey: ["business-locations"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/locations")).data as BusinessLocation[],
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation<BusinessLocation, Error, BusinessLocationCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/locations", data))
        .data as BusinessLocation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-locations"] }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation<
    BusinessLocation,
    Error,
    {
      id: string;
      data: Partial<BusinessLocationCreate> & { is_active?: boolean };
    }
  >({
    mutationFn: async ({ id, data }) =>
      (await apiClient.put(`/api/v1/locations/${id}`, data))
        .data as BusinessLocation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-locations"] }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/api/v1/locations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-locations"] }),
  });
}

export function useSetPrimaryLocation() {
  const qc = useQueryClient();
  return useMutation<BusinessLocation, Error, string>({
    mutationFn: async (id) =>
      (await apiClient.post(`/api/v1/locations/${id}/set-primary`))
        .data as BusinessLocation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-locations"] }),
  });
}
